// src/screens/seller/notifications/SellerNotificationsList.jsx
import React, { useEffect, useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Container, Row, Col } from "react-bootstrap";
import Swal from "sweetalert2";
import {
  MdNotifications,
  MdSearch,
  MdClose,
  MdVisibility,
  MdMarkEmailRead,
  MdMarkEmailUnread,
  MdDelete,
  MdEdit,
} from "react-icons/md";

// Reuse Components — Seller's own only
import Pagination from "../../../components/general/pagination/Pagination";
import StatCard from "../../../components/seller/cards/stats/StatCard";
import ActionDropdown from "../../../components/seller/dropdowns/action/ActionDropdowns";

// CSS
import "../../../assets/css/seller/notifications/SellerNotifications.css";
import SellerLayout from "../../../layouts/SellerLayout";
import MyContext from "../../../context/MyContext";
import { NotificationsPageSkeleton } from "../../../components/general/skeleton/SkeletonBlock";
import { getEcho } from "../../../components/general/echo/Echo";

const parseServerDateTime = (value) => {
  if (!value) return null;
  const match = String(value).match(
    /(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/,
  );
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
  };
};

// Table/modal mein dikhane ke liye "YYYY-MM-DD"
const formatServerDate = (value) => {
  const p = parseServerDateTime(value);
  if (!p) return "";
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(
    p.day,
  ).padStart(2, "0")}`;
};

// Table/modal mein dikhane ke liye "hh:mm AM/PM"
const formatServerTime = (value) => {
  const p = parseServerDateTime(value);
  if (!p) return "";
  const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  const ampm = p.hour >= 12 ? "PM" : "AM";
  return `${String(hour12).padStart(2, "0")}:${String(p.minute).padStart(
    2,
    "0",
  )} ${ampm}`;
};

// Edit form ke <input type="time"> field ke liye 24-hour "HH:mm"
const formatServerTime24 = (value) => {
  const p = parseServerDateTime(value);
  if (!p) return "";
  return `${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(
    2,
    "0",
  )}`;
};

function SellerNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Notifications");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const [editStatus, setEditStatus] = useState("unread");
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [saving, setSaving] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const { baseUrl, authToken, loggedInSeller } = useContext(MyContext);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  //----------API Calling
  const viewAllNotifications = () => {
    setLoading(true);

    axios
      .get(`${baseUrl}/seller/notifications/viewAllNotifications`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setNotifications(response.data.notifications || []);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load notifications.",
        });
      });
  };

  const changeNotificationStatus = (notifications) => {
    const formData = new FormData();
    formData.append("id", notifications.id);

    axios
      .post(
        `${baseUrl}/seller/notifications/changeNotificationStatus`,
        formData,
        { headers: { Authorization: `Bearer ${authToken}` } },
      )
      .then((response) => {
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Notification status successfully updated!",
          });
          viewAllNotifications();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const deleteNotification = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This notification will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("id", id);
        axios
          .post(
            `${baseUrl}/seller/notifications/deleteNotification`,
            formData,
            {
              headers: { Authorization: `Bearer ${authToken}` },
            },
          )
          .then((response) => {
            if (response.data.success == true) {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: "Notification deleted successfully!",
              });
              viewAllNotifications();
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to delete notification.",
              });
            }
          })
          .catch((error) => {
            console.log(error);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to delete notifications.",
            });
          });
      }
    });
  };

  const openEditModal = (notification) => {
    setEditTitle(notification.title || "");
    setEditMessage(notification.message || "");
    setEditStatus(
      String(notification.status || "unread").toLowerCase() === "read"
        ? "read"
        : "unread",
    );
    // ── Date/Time jaan-boojh kar khaali rakhi jati hai (purani value se
    // pre-fill NAHI karte). Agar user isay chhue hi nahi aur Save kar de,
    // to backend current PC time use karega. Agar user khud koi date/time
    // type kare, to wahi save hogi ──
    setEditDate("");
    setEditTime("");
    setSelectedNotification(notification);
    setShowModal(true);
    setIsEditing(true);
  };

  const saveEditedNotification = () => {
    if (!selectedNotification) return;
    if (!editTitle.trim() || !editMessage.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing fields",
        text: "Title aur message dono zaroori hain.",
      });
      return;
    }
    setSaving(true);
    const formData = new FormData();
    formData.append("id", selectedNotification.id);
    formData.append("title", editTitle);
    formData.append("message", editMessage);
    formData.append("status", editStatus);
    formData.append("date", editDate);
    formData.append("time", editTime);

    // ── Ye hamesha bhejte hain — browser (PC) ka apna current date/time.
    // Agar user ne date/time field khali chhodi hai, backend server ka
    // waqt lene ki bajaye ISI value ko fallback ke tor par use karega,
    // taake PC ke actual clock se hi match ho (server ki timezone se
    // koi farq na pade) ──
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    formData.append(
      "client_date",
      `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    );
    formData.append("client_time", `${pad(now.getHours())}:${pad(now.getMinutes())}`);

    axios
      .post(`${baseUrl}/seller/notifications/editNotification`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setSaving(false);
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Notification updated successfully!",
          });
          setShowModal(false);
          setIsEditing(false);
          viewAllNotifications();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.message || "Failed to update notification.",
          });
        }
      })
      .catch((error) => {
        setSaving(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update notification.",
        });
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/seller/login");
      return;
    }
    viewAllNotifications();
  }, [authToken]);

  useEffect(() => {
    if (!authToken || !loggedInSeller?.id) return;

    const echo = getEcho(baseUrl, authToken);
    if (!echo) return;

    const channelName = `seller.${loggedInSeller.id}`;
    const channel = echo.private(channelName);

    channel.listen('.new-notification', (e) => {
      const incoming = e.notification;
      setNotifications((prev) => {
        if (prev.some((n) => n.id === incoming.id)) return prev;
        return [incoming, ...prev];
      });
    });

    return () => {
      echo.leave(channelName);
    };
  }, [authToken, loggedInSeller?.id]);
  useEffect(() => {
    if (loading) return;
    const incoming = location.state?.openNotification;
    if (!incoming) return;

    const match = notifications.find((n) => n.id === incoming.id) || incoming;
    setSelectedNotification(match);
    setIsEditing(false);
    setShowModal(true);

    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, location.state]);

  const stats = [
    {
      label: "Total Notifications",
      value: notifications.length.toString(),
      icon: <MdNotifications />,
      color: "purple",
    },
    {
      label: "Unread",
      value: notifications
        .filter((n) => String(n.status).toLowerCase() !== "read")
        .length.toString(),
      icon: <MdNotifications />,
      color: "orange",
    },
    {
      label: "Read",
      value: notifications.filter((n) => String(n.status).toLowerCase() === "read").length.toString(),
      icon: <MdNotifications />,
      color: "green",
    },
  ];

  // Filters
  const filteredNotifications = (notifications || []).filter((notification) => {
    const matchesSearch =
      notification.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "All Notifications" ||
      (statusFilter === "Unread"
        ? String(notification.status).toLowerCase() !== "read"
        : String(notification.status).toLowerCase() === "read");
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentNotifications = filteredNotifications.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  if (loading) {
    return (
      <SellerLayout>
        <NotificationsPageSkeleton />
      </SellerLayout>
    );
  }

  return (
    <SellerLayout>
      <div className="seller-notifications-page">
        <Container fluid>
          {/* Stats Cards */}
          <Row className="g-3 mb-4">
            {stats.map((stat, index) => (
              <Col key={index} xs={12} sm={6} lg={4}>
                <StatCard {...stat} />
              </Col>
            ))}
          </Row>

          {/* Filters */}
          <Row className="mb-3">
            <Col xs={12}>
              <div className="seller-filters-wrapper">
                <Row className="g-3 align-items-center">
                  <Col xs={12} md={8} lg={9}>
                    <div className="seller-search-box">
                      <span className="seller-search-icon">
                        <MdSearch />
                      </span>
                      <input
                        type="text"
                        className="seller-form-control"
                        placeholder="Search notifications..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                  </Col>
                  <Col xs={12} md={4} lg={3}>
                    <select
                      className="seller-form-control seller-select"
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option>All Notifications</option>
                      <option>Unread</option>
                      <option>Read</option>
                    </select>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          {/* Notifications Table */}
          <Row>
            <Col xs={12}>
              <div className="seller-table-responsive">
                <table className="seller-table">
                  <thead className="seller-table-head">
                    <tr>
                      <th>ID</th>
                      <th>Title</th>
                      <th>Message</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="seller-table-body">
                    {currentNotifications.map((n) => (
                      <tr
                        key={n.id}
                        className={
                          String(n.status).toLowerCase() !== "read" ? "seller-unread-row" : ""
                        }
                      >
                        <td>{n.id}</td>
                        <td>{n.title}</td>
                        <td className="seller-message-preview">{n.message}</td>
                        <td>{formatServerDate(n.created_at || n.date)}</td>
                        <td>{formatServerTime(n.created_at)}</td>
                        <td>
                          <span
                            className={`seller-badge ${
                              String(n.status).toLowerCase() === "read"
                                ? "seller-badge-success"
                                : "seller-badge-warning"
                            }`}
                          >
                            {n.status}
                          </span>
                        </td>
                        <td>
                          <ActionDropdown
                            isOpen={openActionMenu === n.id}
                            onToggle={() =>
                              setOpenActionMenu(
                                openActionMenu === n.id ? null : n.id,
                              )
                            }
                            actions={[
                              {
                                label: "View",
                                icon: <MdVisibility />,
                                onClick: () => {
                                  setSelectedNotification(n);
                                  setIsEditing(false);
                                  setShowModal(true);
                                },
                              },
                              {
                                label: "Edit",
                                icon: <MdEdit />,
                                onClick: () => openEditModal(n),
                              },
                              {
                                label:
                                  String(n.status).toLowerCase() === "read"
                                    ? "Mark as Unread"
                                    : "Mark as Read",
                                onClick: () => changeNotificationStatus(n),
                                icon:
                                  String(n.status).toLowerCase() === "read" ? (
                                    <MdMarkEmailUnread />
                                  ) : (
                                    <MdMarkEmailRead />
                                  ),
                              },
                              {
                                label: "Delete",
                                icon: <MdDelete />,
                                onClick: () => deleteNotification(n.id),
                                variant: "danger",
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredNotifications.length === 0 && (
                  <div className="seller-no-data">No notifications found</div>
                )}
              </div>
            </Col>
          </Row>

          {/* Pagination */}
          <Row>
            <Col xs={12}>
              <Pagination
                totalItems={filteredNotifications.length}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(value) => {
                  setItemsPerPage(value);
                  setCurrentPage(1);
                }}
              />
            </Col>
          </Row>
        </Container>

        {/* Notification Modal — view ya edit, isEditing decide karta hai */}
        {showModal && selectedNotification && (
          <div
            className="seller-modal-overlay"
            onClick={() => {
              setShowModal(false);
              setIsEditing(false);
            }}
          >
            <div
              className="seller-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="seller-modal-header">
                <h3 className="seller-modal-title">
                  {isEditing ? "Edit Notification" : "Notification Details"}
                </h3>
                <button
                  className="seller-modal-close"
                  onClick={() => {
                    setShowModal(false);
                    setIsEditing(false);
                  }}
                >
                  <MdClose />
                </button>
              </div>

              {isEditing ? (
                <>
                  <div className="seller-modal-body">
                    <div className="mb-3">
                      <label className="seller-form-label">
                        Notification ID
                      </label>
                      <input
                        type="text"
                        className="seller-form-control"
                        value={selectedNotification.id}
                        disabled
                        readOnly
                      />
                    </div>

                    <div className="mb-3">
                      <label className="seller-form-label">Title</label>
                      <input
                        type="text"
                        className="seller-form-control"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="seller-form-label">Message</label>
                      <textarea
                        className="seller-form-control"
                        rows={3}
                        value={editMessage}
                        onChange={(e) => setEditMessage(e.target.value)}
                      />
                    </div>

                    <Row className="g-3">
                      <Col xs={6}>
                        <label className="seller-form-label">Date</label>
                        <input
                          type="date"
                          className="seller-form-control"
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                        />
                      </Col>

                      <Col xs={6}>
                        <label className="seller-form-label">Time</label>
                        <input
                          type="time"
                          className="seller-form-control"
                          value={editTime}
                          onChange={(e) => setEditTime(e.target.value)}
                        />
                      </Col>
                    </Row>
                    <small className="text-muted d-block mt-1">
                      Khaali chhod dein to aap ke PC ka current date/time save ho jayega.
                    </small>

                    <div className="mt-3">
                      <label className="seller-form-label">Status</label>
                      <select
                        className="seller-form-control seller-select"
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                      >
                        <option value="unread">Unread</option>
                        <option value="read">Read</option>
                      </select>
                    </div>
                  </div>

                  <div className="seller-modal-footer">
                    <button
                      className="custom-btn custom-btn-outline"
                      onClick={() => {
                        setShowModal(false);
                        setIsEditing(false);
                      }}
                      disabled={saving}
                    >
                      Cancel
                    </button>

                    <button
                      className="custom-btn custom-btn-primary"
                      onClick={saveEditedNotification}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="seller-modal-body">
                    <p>
                      <strong>Title:</strong> {selectedNotification.title}
                    </p>
                    <p>
                      <strong>Message:</strong> {selectedNotification.message}
                    </p>
                    <p>
                      <strong>Date:</strong>{" "}
                      {formatServerDate(selectedNotification.created_at)} at{" "}
                      {formatServerTime(selectedNotification.created_at)}
                    </p>
                    <p>
                      <strong>Status:</strong> {selectedNotification.status}
                    </p>
                  </div>
                  <div className="seller-modal-footer">
                    <button
                      className="custom-btn custom-btn-primary"
                      onClick={() => openEditModal(selectedNotification)}
                    >
                      Edit
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </SellerLayout>
  );
}

export default SellerNotifications;
